/**
 * Shared Swiper helpers for the eila and portal themes.
 *
 * The slider modules of both themes are byte identical; everything that is not
 * theme specific belongs here so a fix has to be made once. Keep this file free
 * of a Swiper import - the hand built pagination of horizontalInfoSlides uses it
 * too.
 */

/** Maximum number of pagination bullets rendered at the same time. */
export const MAX_PAGINATION_BULLETS = 7;

const HIDDEN_CLASS = 'swiper-pagination-bullet--hidden';
const EDGE_CLASS = 'swiper-pagination-bullet--edge';
const ACTIVE_CLASS = 'swiper-pagination-bullet-active';
const CLONE_CLASS = 'swiper-slide-clone';

/**
 * Counts the real slides of a container, ignoring the blank placeholders some
 * sitepart templates render and the copies cloneSlidesForLoop() appends.
 */
export function countSlides(container) {
    if (!container) {
        return 0;
    }

    const wrapper = container.querySelector('.swiper-wrapper') || container;

    return wrapper.querySelectorAll(`.swiper-slide:not(.swiper-slide-blank):not(.${CLONE_CLASS})`).length;
}

/**
 * Number of slides Swiper 11 needs before it keeps loop mode running. Below
 * that it logs a warning and silently falls back to a finite slider.
 */
export function requiredLoopSlides(slidesPerView) {
    return Math.ceil(slidesPerView) * 2;
}

/** Largest slidesPerView a config can reach, across all breakpoints. */
function largestSlidesPerView(config) {
    const values = [config.slidesPerView];

    for (const breakpoint of Object.values(config.breakpoints || {})) {
        values.push(breakpoint.slidesPerView);
    }

    const numbers = values.filter(value => typeof value === 'number' && value > 0);

    return numbers.length ? Math.max(...numbers) : 1;
}

/**
 * Repeats the whole slide set until Swiper has enough slides for loop mode.
 *
 * Repeating the complete set keeps the copies aligned with the originals, so
 * realIndex maps back onto an original slide with a modulo - see realIndexOf().
 */
function cloneSlidesForLoop(container, required) {
    const wrapper = container.querySelector('.swiper-wrapper');

    if (!wrapper) {
        return;
    }

    const originals = Array.from(wrapper.children).filter(child => child.classList.contains('swiper-slide'));

    if (!originals.length) {
        return;
    }

    let total = originals.length;

    while (total < required) {
        for (const slide of originals) {
            const clone = slide.cloneNode(true);

            clone.classList.add(CLONE_CLASS);
            clone.removeAttribute('id');

            for (const element of clone.querySelectorAll('[id]')) {
                element.removeAttribute('id');
            }

            wrapper.appendChild(clone);
            total += 1;
        }
    }
}

/** Drops the copies of an earlier run so re-initialising does not pile them up. */
function removeClones(container) {
    for (const clone of container.querySelectorAll(`.${CLONE_CLASS}`)) {
        clone.remove();
    }
}

/**
 * Aligns a Swiper config with the number of slides actually present.
 *
 * A peek layout (slidesPerView 1.8) relies on the loop to carry the slides
 * around. Swiper 11 drops loop mode below twice the visible slides, and the
 * trailing slides then never reach the front: arrows stop, the click on the
 * neighbouring slide does nothing and a coupled content slider stays put. So
 * instead of degrading the layout, the slide set is repeated until the loop
 * holds - with two images the rear one moves to the front as the design
 * intends.
 *
 * Fewer slides than fit on screen means there is nothing to rotate; loop stays
 * off and Swiper locks the controls itself. Configs that never asked for a loop
 * are left alone.
 */
export function resolveSliderLayout(container, config) {
    if (!container) {
        return {...config};
    }

    removeClones(container);

    const slideCount = countSlides(container);
    const slidesPerView = largestSlidesPerView(config);
    const loop = Boolean(config.loop) && slideCount > slidesPerView;

    if (slideCount <= 1) {
        container.classList.add('is-single');
    }

    if (loop) {
        cloneSlidesForLoop(container, requiredLoopSlides(slidesPerView));
    }

    return {...config, loop};
}

/** Index of the original slide behind the current position, copies folded back. */
export function realIndexOf(swiper) {
    const realCount = countSlides(swiper.el);

    return realCount ? swiper.realIndex % realCount : swiper.realIndex;
}

/**
 * Keeps at most max bullets visible and moves that window along with the active
 * slide. The outermost bullet of the window is marked as an edge while further
 * slides follow behind it - that is the hint that the list continues.
 */
export function limitBullets(bullets, activeIndex, max = MAX_PAGINATION_BULLETS) {
    const list = Array.from(bullets || []);

    if (!list.length) {
        return;
    }

    if (list.length <= max) {
        for (const bullet of list) {
            bullet.classList.remove(HIDDEN_CLASS, EDGE_CLASS);
        }

        return;
    }

    const start = Math.min(Math.max(activeIndex - Math.floor(max / 2), 0), list.length - max);
    const end = start + max - 1;

    list.forEach((bullet, index) => {
        const visible = index >= start && index <= end;
        const isEdge = visible && ((index === start && start > 0) || (index === end && end < list.length - 1));

        bullet.classList.toggle(HIDDEN_CLASS, !visible);
        bullet.classList.toggle(EDGE_CLASS, isEdge);
    });
}

/**
 * Wires limitBullets into a Swiper instance. Swiper renders one bullet per
 * slide, so copies added for the loop get their bullet dropped and the active
 * state folded back onto the original. Swiper rebuilds its bullets on every
 * pagination update, so all of this has to be re-applied each time.
 */
export function limitSwiperPagination(swiper, max = MAX_PAGINATION_BULLETS) {
    if (!swiper || !swiper.pagination) {
        return;
    }

    const apply = () => {
        const all = Array.from(swiper.pagination.bullets || []);

        if (!all.length) {
            return;
        }

        const realCount = Math.min(countSlides(swiper.el) || all.length, all.length);
        const real = all.slice(0, realCount);
        const activeIndex = swiper.realIndex % realCount;

        for (const bullet of all.slice(realCount)) {
            bullet.classList.add(HIDDEN_CLASS);
            bullet.classList.remove(ACTIVE_CLASS, EDGE_CLASS);
        }

        real.forEach((bullet, index) => bullet.classList.toggle(ACTIVE_CLASS, index === activeIndex));

        limitBullets(real, activeIndex, max);
    };

    swiper.on('paginationRender', apply);
    swiper.on('paginationUpdate', apply);
    apply();
}
